import React from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './KnightsTrainCategories.css';

const KnightsTrainCategories = () => {
    const navigate = useNavigate();

    // Knights trainer category options with medieval themes
    const knightsTrainerCategories = [
        {
            id: 'knights-one-trainers',
            title: 'Legendary Knights of the Round Table',
            description: 'Train with the greatest champions of Camelot and beyond',
            image: '/assets/images/knights-one-trainers.jpg',
            route: '/knights-one-trainers',
            color: '#C0C0C0', // Silver
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Tournament Mastery', 'Sword Excellence', 'Grail Quest Training']
        },
        {
            id: 'knights-two-trainers',
            title: 'Mysterious & Complex Knights',
            description: 'Train with the enigmatic warriors of legend and shadow',
            image: '/assets/images/knights-two-trainers.jpg',
            route: '/knights-two-trainers',
            color: '#8B0000', // Dark red
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Artistic Warfare', 'Maximum Strength', 'Sacred Endurance']
        },
        {
            id: 'knights-three-trainers',
            title: 'Specialized & Unique Knights',
            description: 'Train with the most distinctive warriors of the realm',
            image: '/assets/images/knights-three-trainers.jpg',
            route: '/knights-three-trainers',
            color: '#4B0082', // Indigo
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Administrative Excellence', 'Primal Transformation', 'Joyful Resilience']
        },
        {
            id: 'knights-four-trainers',
            title: 'Exotic & International Knights',
            description: 'Train with warriors from distant lands and diverse backgrounds',
            image: '/assets/images/knights-four-trainers.jpg',
            route: '/knights-four-trainers',
            color: '#800080', // Purple
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Merit-Based Excellence', 'Cultural Fusion', 'Fearless Courage']
        },
        {
            id: 'knights-five-trainers',
            title: 'Regional Lords & Ancient Bloodlines',
            description: 'Train with the noble rulers and keepers of ancient traditions',
            image: '/assets/images/knights-five-trainers.jpg',
            route: '/knights-five-trainers',
            color: '#4169E1', // Royal blue
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Legacy Building', 'Regional Leadership', 'Cultural Bridge']
        }
    ];

    // Handle trainer category selection
    const handleCategorySelect = (category) => {
        console.log('Knights trainer category clicked:', category);

        // 🎵 PLAY CATEGORY-SPECIFIC SOUND
        if (category.sound) {
            soundManager.playSound(category.sound, 0.7);
        } else {
            soundManager.playButtonSound();
        }

        // Navigate to the specific Knights trainer category page
        navigate(category.route);
    };

    // Handle back to home
    const handleBackToHome = () => {
        soundManager.playButtonSound();
        navigate('/');
    };

    return (
        <div className="knights-train-categories-container">
            <div className="knights-animated-background"></div>

            <header className="knights-train-categories-header">
                <button
                    className="back-to-home-button knights-back-button"
                    onClick={handleBackToHome}
                >
                    ← Back to Home
                </button>

                <div className="header-content">
                    <h1>Medieval Knights Training Masters</h1>
                    <p>Choose your path to train with legendary warriors of chivalry and honor</p>
                    <div className="knights-symbol">⚔️</div>
                </div>
            </header>

            <div className="knights-trainer-categories-grid">
                {knightsTrainerCategories.map((category) => (
                    <div
                        key={category.id}
                        className="knights-trainer-category-card-link"
                        onClick={() => handleCategorySelect(category)}
                    >
                        <div
                            className="knights-trainer-category-card"
                            data-category-id={category.id}
                            style={{
                                '--knights-theme-color': category.color
                            }}
                        >
                            <div className="knights-trainer-category-image">
                                <div
                                    className="knights-image-placeholder"
                                    style={{
                                        backgroundColor: category.color,
                                        backgroundImage: `url(${category.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    <div className="knights-trainer-count-badge">
                                        {category.trainerCount} Knights
                                    </div>
                                    <div className="knights-overlay"></div>
                                </div>
                            </div>
                            <div className="knights-trainer-category-info">
                                <h2 style={{ color: category.color }}>{category.title}</h2>
                                <p>{category.description}</p>
                                <div className="knights-category-features">
                                    {category.features.map((feature, index) => (
                                        <span key={index} className="knights-feature-badge">
                                            ⚔️ {feature}
                                        </span>
                                    ))}
                                </div>
                                <div className="knights-theme-indicator" style={{ backgroundColor: category.color }}></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <footer className="knights-train-categories-footer">
                <div className="knights-train-info">
                    <h3>⚔️ Medieval Knights Training Philosophy</h3>
                    <div className="knights-info-grid">
                        <div className="knights-info-item">
                            <span className="knights-info-icon">🏰</span>
                            <div>
                                <strong>Legendary Warriors</strong>
                                <p>Train with 30 legendary knights from Arthurian legend and medieval lore</p>
                            </div>
                        </div>
                        <div className="knights-info-item">
                            <span className="knights-info-icon">🛡️</span>
                            <div>
                                <strong>Honor & Strength</strong>
                                <p>Experience training that combines physical prowess with noble character</p>
                            </div>
                        </div>
                        <div className="knights-info-item">
                            <span className="knights-info-icon">👑</span>
                            <div>
                                <strong>Chivalric Heritage</strong>
                                <p>Connect with centuries of knightly tradition and the code of chivalry</p>
                            </div>
                        </div>
                        <div className="knights-info-item">
                            <span className="knights-info-icon">⚡</span>
                            <div>
                                <strong>Diverse Mastery</strong>
                                <p>From tournament champions to grail knights, master every aspect of knightly combat</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="knights-flow-info">
                    <div className="knights-divider"></div>
                    <p>💡 <strong>Path:</strong> Home → Knights Train → Orders → Knight → Intensity → Workout</p>
                    <p className="knights-wisdom">"Strength does not come from physical capacity. It comes from an indomitable will" - Code of Chivalry</p>
                </div>
            </footer>
        </div>
    );
};

export default KnightsTrainCategories;